import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="bill-item-bulk-actions"
//
// Drives the bulk-action bar on the new coder review (inpatient itemized) UI:
//   - shows the "X items selected" bar + Verify/Flag buttons when any child
//     row checkbox is checked
//   - lets the header's master checkbox toggle every visible item checkbox
//   - keeps the hidden bill_item_ids[] inputs on the bulk Verify form in sync
//     with the actual selection
//   - opens the bulk-flag drawer with the current selection as a query string
export default class extends Controller {
  static targets = ["actionBar", "count", "masterCheckbox", "itemCheckbox", "verifyForm"]
  static values = { billId: Number }

  connect() {
    this.updateState()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.itemCheckboxTargets.forEach((cb) => {
      // Skip checkboxes whose row is hidden (collapsed) so the user doesn't
      // accidentally bulk-act on rows they cannot see.
      const row = cb.closest("tr")
      if (row && row.classList.contains("hidden")) return
      cb.checked = checked
    })
    this.updateState()
  }

  updateState() {
    const selected = this.selectedIds()
    if (this.hasCountTarget) this.countTarget.textContent = selected.length

    if (this.hasActionBarTarget) {
      if (selected.length > 0) {
        this.actionBarTarget.classList.remove("hidden")
        this.actionBarTarget.classList.add("flex")
      } else {
        this.actionBarTarget.classList.add("hidden")
        this.actionBarTarget.classList.remove("flex")
      }
    }

    // Sync the master checkbox indeterminate / checked state with the visible items.
    if (this.hasMasterCheckboxTarget) {
      const visible = this.itemCheckboxTargets.filter((cb) => {
        const row = cb.closest("tr")
        return !(row && row.classList.contains("hidden"))
      })
      const checked = visible.filter((cb) => cb.checked)
      if (visible.length === 0) {
        this.masterCheckboxTarget.checked = false
        this.masterCheckboxTarget.indeterminate = false
      } else if (checked.length === visible.length) {
        this.masterCheckboxTarget.checked = true
        this.masterCheckboxTarget.indeterminate = false
      } else if (checked.length === 0) {
        this.masterCheckboxTarget.checked = false
        this.masterCheckboxTarget.indeterminate = false
      } else {
        this.masterCheckboxTarget.checked = false
        this.masterCheckboxTarget.indeterminate = true
      }
    }

    // Make sure the bulk Verify form ships the current selection on submit.
    // Also clear any stale `disabled` on the submit button. Turbo sets it
    // during in-flight submission and normally a fresh page render reseats
    // the button, but this button lives inside an initially-hidden actionBar
    // and Turbo Drive's bf-cache can restore the disabled state from a prior
    // submission, leaving the button stuck. Resetting it on every selection
    // change is a cheap belt-and-suspenders.
    if (this.hasVerifyFormTarget) {
      const verifyBtn = this.verifyFormTarget.querySelector("button[type='submit']")
      if (verifyBtn) verifyBtn.disabled = false
      this.syncHiddenIds(this.verifyFormTarget, selected)
    }
  }

  openFlagDrawer(event) {
    event.preventDefault()
    const selected = this.selectedIds()
    if (selected.length === 0) return

    const qs = selected.map((id) => `bill_item_ids[]=${encodeURIComponent(id)}`).join("&")
    const url = `/coders/bills/${this.billIdValue}/flag_bill_items_form?${qs}`

    const ev = new Event("drawer.open")
    ev.url = url
    document.dispatchEvent(ev)
  }

  selectedIds() {
    return this.itemCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
  }

  syncHiddenIds(form, ids) {
    // Remove old hidden inputs added by us, then re-add fresh ones.
    form.querySelectorAll("input[data-bulk-id]").forEach((el) => el.remove())
    ids.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "bill_item_ids[]"
      input.value = id
      input.dataset.bulkId = "true"
      form.appendChild(input)
    })
  }
}
